import axios, { CancelTokenSource } from 'axios';

import { APIError, UnsupportedStructureError } from '~services/Errors';

import {
  CampaignLeadListResponse,
  CampaignLeadListResponseDecoder,
  CreateCampaignLeadList,
  FilterType,
} from './domain';

export const getCampaignListsByNextUrl = async (
  nextUrl: string,
  cancelToken?: CancelTokenSource,
): Promise<CampaignLeadListResponse | undefined> => {
  const path = nextUrl;
  let resp;

  try {
    resp = await axios.request({
      url: path,
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
      cancelToken: cancelToken ? cancelToken.token : undefined,
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return undefined;
    }

    if (axios.isAxiosError(e)) {
      // Response should always be defined if axios error
      throw new APIError(e.response!.status, (e.response!.data as { error: string }).error || e.message);
    }

    throw new APIError(-1, e as string);
  }

  const decoded = CampaignLeadListResponseDecoder.run(resp.data);

  if (decoded.ok === false) {
    const err = new UnsupportedStructureError(decoded.error.message);

    console.error(decoded.error);
    console.error(err);
    throw err;
  }

  return decoded.result;
};

export const getCampaignLists = async ({
  campaignId,
  search,
  filters,
  cancelToken,
}: {
  campaignId: number;
  search?: string;
  filters?: FilterType[];
  cancelToken?: CancelTokenSource;
}): Promise<CampaignLeadListResponse | undefined> => {
  const path = `/api/internal/campaign/${campaignId}/lists`;
  let resp;
  try {
    resp = await axios.request({
      url: path,
      method: 'GET',
      params: {
        search: search?.toLowerCase() || undefined,
        filters: filters,
      },
      paramsSerializer: {
        indexes: null,
      },
      headers: {
        Accept: 'application/json',
      },
      cancelToken: cancelToken?.token,
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return undefined;
    }

    if (axios.isAxiosError(e)) {
      // Response should always be defined if axios error
      throw new APIError(e.response!.status, (e.response!.data as { error: string }).error || e.message);
    }

    throw new APIError(-1, e as string);
  }

  const decoded = CampaignLeadListResponseDecoder.run(resp.data);

  if (decoded.ok === false) {
    const err = new UnsupportedStructureError(decoded.error.message);

    console.error(decoded.error);
    console.error(err);
    throw err;
  }

  return decoded.result;
};

export const createList = async (campaignId: number, data: CreateCampaignLeadList): Promise<void> => {
  const path = `/api/campaign/${campaignId}/list`;

  const body = {
    name: data.name,
    description: data.description,
    priority: data.priority,
    start_time: data.startTime,
    end_time: data.endTime,
  };

  try {
    await axios.request({
      method: 'POST',
      url: path,
      data: body,
      headers: {
        Accept: 'application/json',
      },
    });
  } catch (e) {
    if (axios.isAxiosError(e)) {
      // Response should always be defined if axios error
      throw new APIError(e.response!.status, e.message);
    }

    throw new APIError(-1, e as string);
  }
};

export const toggleLeadsListActiveById = async (campaignId: number, listId: number, isActive: boolean) => {
  const path = `/api/campaign/${campaignId}/list/${listId}`;

  const body = {
    is_active: isActive,
  };

  try {
    await axios.request({
      method: 'PATCH',
      url: path,
      data: body,
      headers: {
        Accept: 'application/json',
      },
    });
  } catch (e) {
    if (axios.isAxiosError(e)) {
      // Response should always be defined if axios error
      throw new APIError(e.response!.status, e.message);
    }

    throw new APIError(-1, e as string);
  }
};
